<!-- Hero Section -->
<section class="hero">
  <div class="container">
    <h1 class="hero__title">{{ 'pages.marketPredictions.title' | transloco }}</h1>
    <p class="hero__description">{{ 'pages.marketPredictions.description' | transloco }}</p>
  </div>
</section>

<!-- Main Content Section - Always visible -->
<section class="content-section">
  <div class="container">

    <!-- SSR Placeholder (shown during server-side rendering) -->
    <div *ngIf="!isBrowser" class="ssr-placeholder">
      <p>{{ 'predictions.loading' | transloco }}</p>
    </div>

    <!-- Browser Content -->
    <ng-container *ngIf="isBrowser">
      <!-- Loading State -->
      <div *ngIf="loading" class="loading-container">
        <mat-spinner diameter="60"></mat-spinner>
        <p class="loading-text">{{ 'predictions.loading' | transloco }}</p>
      </div>

      <!-- Error State -->
      <mat-card *ngIf="!loading && error" class="error-card">
        <i class="fas fa-exclamation-circle error-icon"></i>
        <h2>{{ 'predictions.error.title' | transloco }}</h2>
        <p>{{ 'predictions.error.message' | transloco }}</p>
      </mat-card>

      <!-- Data Content -->
      <ng-container *ngIf="!loading && !error && predictions">
        <!-- Prediction Info Header -->
        <div class="prediction-header">
      <div class="prediction-meta">
        <span class="prediction-month">
          <i class="fas fa-calendar-alt"></i>
          {{ 'predictions.predictionFor' | transloco }}: {{ formatMonth(predictions.predictionForMonth) }}
        </span>
        <span class="prediction-generated">
          <i class="fas fa-clock"></i>
          {{ 'predictions.generatedAt' | transloco }}: {{ formatDate(predictions.generatedAt) }}
        </span>
      </div>
    </div>

    <!-- Legal Disclaimer -->
    <mat-card class="disclaimer-card">
      <div class="disclaimer-content">
        <i class="fas fa-info-circle disclaimer-icon"></i>
        <div class="disclaimer-text">
          <strong>{{ 'predictions.disclaimer.title' | transloco }}</strong>
          <p>{{ 'predictions.disclaimer.message' | transloco }}</p>
        </div>
      </div>
    </mat-card>

    <!-- Global Market Overview -->
    <div class="section-header">
      <i class="fas fa-globe section-icon"></i>
      <h2>{{ 'predictions.globalOverview.title' | transloco }}</h2>
    </div>

    <div class="metrics-grid">
      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-briefcase"></i>
          <span class="metric-label">{{ 'predictions.metrics.jobs' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.jobs.toLocaleString() }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.jobs_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.jobs_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.jobs_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-users"></i>
          <span class="metric-label">{{ 'predictions.metrics.candidates' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.candidates.toLocaleString() }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.candidates_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.candidates_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.candidates_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-clipboard-list"></i>
          <span class="metric-label">{{ 'predictions.metrics.applications' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.applications.toLocaleString() }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.applications_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.applications_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.applications_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-arrow-up"></i>
          <span class="metric-label">{{ 'predictions.metrics.hiringRate' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.hiring_rate.toFixed(1) }}%</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.hiring_rate_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.hiring_rate_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.hiring_rate_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-balance-scale"></i>
          <span class="metric-label">{{ 'predictions.metrics.supplyDemandRatio' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.supply_demand_ratio.toFixed(2) }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.supply_demand_ratio_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.supply_demand_ratio_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.supply_demand_ratio_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-plus-circle"></i>
          <span class="metric-label">{{ 'predictions.metrics.jobsCreated' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.jobs_created.toLocaleString() }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.jobs_created_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.jobs_created_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.jobs_created_change_percentage) }}</span>
        </div>
      </mat-card>

      <mat-card class="metric-card">
        <div class="metric-header">
          <i class="fas fa-user-plus"></i>
          <span class="metric-label">{{ 'predictions.metrics.candidatesRegistered' | transloco }}</span>
        </div>
        <div class="metric-value">{{ predictions.predictedState.global.candidates_reg.toLocaleString() }}</div>
        <div class="metric-change" [ngClass]="predictions.predictedState.global.candidates_reg_change_percentage >= 0 ? 'trend-growing' : 'trend-cooling'">
          <i class="fas" [ngClass]="predictions.predictedState.global.candidates_reg_change_percentage >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          <span>{{ formatPercentage(predictions.predictedState.global.candidates_reg_change_percentage) }}</span>
        </div>
      </mat-card>
    </div>

    <!-- Prediction Accuracy Comparison Chart -->
    <div class="section-header" *ngIf="predictions.prevState">
      <i class="fas fa-chart-bar section-icon"></i>
      <h2>{{ 'predictions.chart.title' | transloco }}</h2>
    </div>

    <mat-card class="chart-card" *ngIf="predictions.prevState && comparisonChartData.length > 0">
      <!-- SVG filter for Safari-compatible drop shadow on hover -->
      <svg width="0" height="0" style="position: absolute;">
        <defs>
          <filter id="chart-predictions-drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="3" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.7"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
      </svg>

      <div class="chart-container">
        <ngx-charts-bar-vertical-2d
          [results]="comparisonChartData"
          [scheme]="colorScheme"
          [schemeType]="schemeType"
          [xAxis]="showXAxis"
          [yAxis]="showYAxis"
          [legend]="showLegend"
          [legendPosition]="legendPosition"
          [showXAxisLabel]="showXAxisLabel"
          [showYAxisLabel]="showYAxisLabel"
          [xAxisLabel]="'predictions.chart.xAxisLabel' | transloco"
          [yAxisLabel]="'predictions.chart.yAxisLabel' | transloco"
          [animations]="animations"
          [gradient]="gradient"
          [barPadding]="8"
          [groupPadding]="16">
        </ngx-charts-bar-vertical-2d>
      </div>
    </mat-card>

    <!-- Hot Sectors -->
    <div class="section-header">
      <i class="fas fa-fire section-icon hot-icon"></i>
      <h2>{{ 'predictions.hotSectors.title' | transloco }}</h2>
    </div>

    <!-- Empty state for hot sectors -->
    <mat-card *ngIf="!hasHotSectors()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.hotSectors.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.hotSectors.message' | transloco }}</p>
    </mat-card>

    <div class="sectors-grid" *ngIf="hasHotSectors()">
      <mat-card *ngFor="let sector of predictions.hotSectors" class="sector-card hot-sector">
        <div class="sector-rank">
          <span class="rank-number">#{{ sector.rank }}</span>
        </div>
        <h3 class="sector-name">{{ 'jobCategories.' + sector.category_key | transloco }}</h3>
        <p class="sector-explanation">{{ getTranslatedText(sector.explanation) }}</p>

        <div class="sector-metrics">
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.metrics.jobs' | transloco }}:</span>
            <span class="metric-value">{{ sector.metrics.jobs.toLocaleString() }}</span>
          </div>
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.metrics.candidates' | transloco }}:</span>
            <span class="metric-value">{{ sector.metrics.candidates.toLocaleString() }}</span>
          </div>
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.sectors.change' | transloco }}:</span>
            <span class="metric-value trend-growing">{{ formatPercentage(sector.metrics.change_percentage) }}</span>
          </div>
        </div>
      </mat-card>
    </div>

    <!-- Cooling Sectors -->
    <div class="section-header">
      <i class="fas fa-snowflake section-icon cooling-icon"></i>
      <h2>{{ 'predictions.coolingSectors.title' | transloco }}</h2>
    </div>

    <!-- Empty state for cooling sectors -->
    <mat-card *ngIf="!hasCoolingSectors()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.coolingSectors.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.coolingSectors.message' | transloco }}</p>
    </mat-card>

    <div class="sectors-grid" *ngIf="hasCoolingSectors()">
      <mat-card *ngFor="let sector of predictions.coolingSectors" class="sector-card cooling-sector">
        <div class="sector-rank">
          <span class="rank-number">#{{ sector.rank }}</span>
        </div>
        <h3 class="sector-name">{{ 'jobCategories.' + sector.category_key | transloco }}</h3>
        <p class="sector-explanation">{{ getTranslatedText(sector.explanation) }}</p>

        <div class="sector-metrics">
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.metrics.jobs' | transloco }}:</span>
            <span class="metric-value">{{ sector.metrics.jobs.toLocaleString() }}</span>
          </div>
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.metrics.candidates' | transloco }}:</span>
            <span class="metric-value">{{ sector.metrics.candidates.toLocaleString() }}</span>
          </div>
          <div class="sector-metric">
            <span class="metric-label">{{ 'predictions.sectors.change' | transloco }}:</span>
            <span class="metric-value trend-cooling">{{ formatPercentage(sector.metrics.change_percentage) }}</span>
          </div>
        </div>
      </mat-card>
    </div>

    <!-- Risk Factors -->
    <div class="section-header">
      <i class="fas fa-exclamation-triangle section-icon risk-icon"></i>
      <h2>{{ 'predictions.riskFactors.title' | transloco }}</h2>
    </div>

    <!-- Empty state for risk factors -->
    <mat-card *ngIf="!hasRiskFactors()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.riskFactors.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.riskFactors.message' | transloco }}</p>
    </mat-card>

    <div class="risks-grid" *ngIf="hasRiskFactors()">
      <mat-card *ngFor="let risk of predictions.riskFactors" class="risk-card">
        <div class="risk-header">
          <h3 class="risk-title">{{ getTranslatedText(risk.risk) }}</h3>
          <mat-chip [ngClass]="getSeverityClass(risk.severity)">
            {{ 'predictions.severity.' + risk.severity | transloco }}
          </mat-chip>
        </div>

        <div class="risk-detail">
          <div class="risk-section">
            <h4><i class="fas fa-info-circle"></i> {{ 'predictions.riskFactors.impact' | transloco }}</h4>
            <p>{{ getTranslatedText(risk.impact) }}</p>
          </div>
          <div class="risk-section">
            <h4><i class="fas fa-eye"></i> {{ 'predictions.riskFactors.watchFor' | transloco }}</h4>
            <p>{{ getTranslatedText(risk.watch_for) }}</p>
          </div>
        </div>
      </mat-card>
    </div>

    <!-- Recommendations -->
    <div class="section-header">
      <i class="fas fa-lightbulb section-icon recommend-icon"></i>
      <h2>{{ 'predictions.recommendations.title' | transloco }}</h2>
    </div>

    <!-- Empty state for recommendations -->
    <mat-card *ngIf="!hasRecommendations()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.recommendations.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.recommendations.message' | transloco }}</p>
    </mat-card>

    <div class="recommendations-grid" *ngIf="hasRecommendations()">
      <mat-card *ngFor="let rec of predictions.recommendations" class="recommendation-card">
        <div class="recommendation-header">
          <div>
            <mat-chip class="audience-chip">{{ rec.audience }}</mat-chip>
            <mat-chip [ngClass]="getPriorityClass(rec.priority)">
              {{ 'predictions.priority.' + rec.priority | transloco }}
            </mat-chip>
          </div>
        </div>

        <h3 class="recommendation-title">{{ getTranslatedText(rec.recommendation) }}</h3>
        <p class="recommendation-rationale">{{ getTranslatedText(rec.rationale) }}</p>

        <div class="action-items" *ngIf="rec.action_items && rec.action_items.length > 0">
          <h4><i class="fas fa-tasks"></i> {{ 'predictions.recommendations.actionItems' | transloco }}</h4>
          <ul>
            <li *ngFor="let action of rec.action_items">{{ getTranslatedText(action) }}</li>
          </ul>
        </div>
      </mat-card>
    </div>

    <!-- AI Reasoning -->
    <div class="section-header">
      <i class="fas fa-brain section-icon ai-icon"></i>
      <h2>{{ 'predictions.reasoning.title' | transloco }}</h2>
    </div>

    <!-- Empty state for reasoning -->
    <mat-card *ngIf="!hasReasoning()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.reasoning.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.reasoning.message' | transloco }}</p>
    </mat-card>

    <mat-accordion class="reasoning-accordion" *ngIf="hasReasoning()" multi="true">
      <mat-expansion-panel *ngIf="predictions.reasoning.main_drivers && predictions.reasoning.main_drivers.length > 0" [expanded]="true">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fas fa-arrow-up"></i>
            {{ 'predictions.reasoning.mainDrivers' | transloco }}
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ul class="reasoning-list">
          <li *ngFor="let driver of predictions.reasoning.main_drivers">{{ getTranslatedText(driver) }}</li>
        </ul>
      </mat-expansion-panel>

      <mat-expansion-panel *ngIf="predictions.reasoning.patterns_applied && predictions.reasoning.patterns_applied.length > 0">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fas fa-shapes"></i>
            {{ 'predictions.reasoning.patternsApplied' | transloco }}
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ul class="reasoning-list">
          <li *ngFor="let pattern of predictions.reasoning.patterns_applied">{{ getTranslatedText(pattern) }}</li>
        </ul>
      </mat-expansion-panel>

      <mat-expansion-panel *ngIf="predictions.reasoning.seasonal_adjustments && predictions.reasoning.seasonal_adjustments.length > 0">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fas fa-sun"></i>
            {{ 'predictions.reasoning.seasonalAdjustments' | transloco }}
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ul class="reasoning-list">
          <li *ngFor="let adjustment of predictions.reasoning.seasonal_adjustments">{{ getTranslatedText(adjustment) }}</li>
        </ul>
      </mat-expansion-panel>

      <mat-expansion-panel *ngIf="predictions.reasoning.learnings_applied && predictions.reasoning.learnings_applied.length > 0">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fas fa-graduation-cap"></i>
            {{ 'predictions.reasoning.learningsApplied' | transloco }}
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ul class="reasoning-list">
          <li *ngFor="let learning of predictions.reasoning.learnings_applied">{{ getTranslatedText(learning) }}</li>
        </ul>
      </mat-expansion-panel>
    </mat-accordion>

    <!-- Confidence Analysis -->
    <div class="section-header">
      <i class="fas fa-chart-line section-icon confidence-icon"></i>
      <h2>{{ 'predictions.confidenceAnalysis.title' | transloco }}</h2>
    </div>

    <!-- Empty state for confidence analysis -->
    <mat-card *ngIf="!hasConfidenceAnalysis()" class="empty-state-card">
      <i class="fas fa-info-circle empty-state-icon"></i>
      <h3>{{ 'predictions.emptyStates.confidenceAnalysis.title' | transloco }}</h3>
      <p>{{ 'predictions.emptyStates.confidenceAnalysis.message' | transloco }}</p>
    </mat-card>

    <div class="confidence-grid" *ngIf="hasConfidenceAnalysis()">
      <mat-card class="confidence-card" *ngIf="predictions.confidenceAnalysis.high_confidence_factors && predictions.confidenceAnalysis.high_confidence_factors.length > 0">
        <h3><i class="fas fa-check-double"></i> {{ 'predictions.confidenceAnalysis.highConfidence' | transloco }}</h3>
        <ul class="confidence-list">
          <li *ngFor="let factor of predictions.confidenceAnalysis.high_confidence_factors" class="confidence-item positive">
            <i class="fas fa-check-circle"></i>
            <span>{{ getTranslatedText(factor) }}</span>
          </li>
        </ul>
      </mat-card>

      <mat-card class="confidence-card" *ngIf="predictions.confidenceAnalysis.uncertainty_factors && predictions.confidenceAnalysis.uncertainty_factors.length > 0">
        <h3><i class="fas fa-question-circle"></i> {{ 'predictions.confidenceAnalysis.uncertaintyFactors' | transloco }}</h3>
        <ul class="confidence-list">
          <li *ngFor="let factor of predictions.confidenceAnalysis.uncertainty_factors" class="confidence-item negative">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ getTranslatedText(factor) }}</span>
          </li>
        </ul>
      </mat-card>
    </div>
      </ng-container>
    </ng-container>

  </div>
</section>
